import { Map } from 'immutable';
import {
  ITablelistStateListItemConfig,
} from "../../../app/worklist/interfaces";

export interface IOrdersState {
  // order definitions mapped by order category as a key
  ordersDefinition: IOrderDetailDefinition;
  // currently selected order
  selectedOrder: IOrder;
  // all orders mapped by order id as its key
  ordersData: Map<string, IOrder>;
  // all order details mapped order detail category as its key
  orderDetailsData: Map<string, Array<IOrderDetail>>;
  // collection of selected order details for the selected order
  selectedOrderDetails: Array<IOrderDetail>;
  // active order details filtered from the selectedOrderDetails
  currentActiveOrderDetails: Array<IOrderDetail>;
  // step level status
  stepStatus: IOrderStepStatus;
}

export interface IOrderStepStatus {
  // step level due date to be consumed by workflow
  dueDate?: string;
  // step level status to be consumed by workflow
  status?: any;
}

export interface IOrderDetailDefinition {
  id: string;
  name: string;
  type?: string;
  context?: Array<{ reference: string, resourceType: string }>;
  // type of the resource
  resourceType?: string;
  // used for validation form using client identifier
  apiValidatorConfig?: {
    diagnosticOrder: {
      formIds: Array<{ id: string, identifier: string }>
    };
  };
  workflowJobsTemplates?: Array<{ usage: string, reference: string }>;
  // configurations used to calculate the status and due date
  orderDetailConfig?: {
    tempWorkflowInputs: Array<{ key: string, args: string, name: string, type: string, value: string }>;
    orderValidationFormId: string;
  };
}

export interface ISelectedOrderDefinition {
  formsConfig: Array<{ key: string, title: string, formId: string, formVertical: boolean }>;
  dueDateAndStatus?: Array<any>;
  acrValidationRequired?: boolean;
  orderDetailsRequired?:boolean;
}

export interface IOrder {
  id: string;
  context?: Array<any>;
  meta?: any;
  // type of the resource - Orderinformation
  resourceType?: string;
  // category of the order like CT, PET-CT, MRI.. etc
  category: {
    code: string;
    display: string;
  };
  authorization?: any;
  // collection of associated / selected order detail id(s)
  isActive: Array<string>;
  // reason for the order
  reason?: string;
  // due date of the order - calculated for the first time of order creation
  // if order detail is associated, due date will be derived from the order detail
  dueDate?: string;
  // status of the order - calculated for the first time of order creation
  // if order detail is associated, status will be derived from the order detail
  status: {
    code: string;
    display: string;
  };
  // soft deletion flag. if it is true, will not be considered to be shown in client side
  isDeleted?: boolean;
  source?: string;
  // biodesix order details
  biodesixOrderDetails?: IBiodesixOrderDetails;
}

export interface IOrderManagerConfig {
  // configuration for the create order functionality
  create: {
    // for id to be used for create order popup
    formId: string;
    // title to be displayed in the create order popup
    title: string;
    // configurations like api end point (in case of custom end points)
    backend?: {
      // end point to hit when creating an order
      apiEndPoint: string;
    };
  };
  // configuration for orders list panel
  listPanel: {
    // width of the list panel in %
    width: string;
    // to allow adding a new order
    canAdd?: boolean;
    // title to be displayed at the top of the list panel
    title?: string;
    // to allow deleting the order
    canDelete?: boolean;
    // to hid the list panel, if not necessary to show
    hidden?: boolean;
    // read / write permission to validate the against the current user
    requiredPermissionsIds?: {
      read?: string;
      write?: string;
    };
    // databinding to fetch the order's using bundil util API
    dataBinding: Array<any>;
    // configuration for list items to display in columns title and value
    listItems?: Array<IListItemConfig>;
    // if any order type to be excluded
    orderTypesToExclude?: Array<string>;
    // if workflow latestRevisionStatus and this field is not equal then order edit will be disabled
    lookupJobNameToDisableEdit: string;
  };
  contentPanel: {
    // width of the content panel in %
    width: string;
    // data extraction for saving the content
    extractData?: any;
    // databinding to fetch the order details and update in store
    dataBinding?: Array<any>;
    // properties to be validated when the user is trying to save the order details with out any of the properties defined
    propertiesToValidate?: Array<string | PropertyToValidate>;

    watchPropertyForSaveButton: Array<string>;
    // holds contact details for Biodesix lab test orders
    contactDetails?: IContactDetails;
  };

  dataComposer?: any;
  // only allows user to edit who is assignee to current workflow
  permitOnlyAssignee: boolean;

  readOnlyMode?: boolean;

  contributeToTestBarButtonStatus?: boolean;

  triggerAcrRequired?: boolean;

  updateStatusDueDateOfDiagnosticOrder?: boolean;

  testName: string;
}
export interface PropertyToValidate {
  isOrValidationRequired: boolean;
  validationProperties: Array<string>;
}
export interface IListItemConfig {
  // path of the property to read to display in the relevant column
  path: string;
  // header to be displayed for the relevant column
  label: string;
  // Used to be displayed for the relevant rows
  header: boolean;
  // default value to display when value is not found in the specified path
  defaultValue: string;
}

export interface IOrderDetail {
  // used to store the order status (ex: Draft)
  orderStatus?: string;

  // biodesix lab order status
  labOrderStatus?: string;

  // due date of the order detail calculated from backend using WF mechanism
  dueDate?: string;
  // status of the order detail calculated from backend using singlestep Workflow
  derivedStatus?: {
    code: string;
    display: string
  };
  // indicates whether order detail is active or not
  selected?: boolean;
  id: string;
  meta?: {};
  // type of the resource - Orderdetail
  resourceType?: string;
  // category of the order detail like CT, PET-CT, MRI.. etc
  category: {
    code: string;
    display: string
  };
  // description of the order detail
  description?: string;
  // name of the physician who is ordering
  orderingPersonnel?: {
    firstName: string;
    lastName: string;
    npi: string;
    role: string;
    phoneNumber: string;
  }

  // soft deletion flag. if it is true, will not be considered to be shown in client side
  isDeleted?: boolean;
  // indicates if it is from EMR / manual
  source?: string;
  clientSpecificMap?: [];

  workflowRequest: string;
  // Date on which orderDetail was created
  dateOfTest: string;
  // order Date
  dateOfOrder?: string;
  // give info about scheduleDate
  scheduleInformation?: {
    appointmentId: string,
    appointmentDate: string,
    appointmentTime: string,
    appointmentType: string
  };
  // biodesix order created by and on details
  biodesixOrderingPersonnel?: IBiodesixOrderingPersonnel;
  identifiers: { value: string }[];
}

export interface IBiodesixOrderDetails {
  // biodesix order status
  orderStatus?: string;
  // biodesix order submission date
  submissionDate?: string;
}

// biodesix order created by and on details
export interface IBiodesixOrderingPersonnel {
  email?: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  reference?: string;
  signedDate?: string;
}

// biodesix order status
export enum BiodesixOrderStatus {
  CANCELED = 'Canceled',
  COMPLETED = 'Completed',
  ERROR = 'Error',
  IN_PROCESS = 'In progress',
  INITIAL = 'Initial',
  ON_HOLD = 'On hold'
}

export interface IContactDetails {
  contactNote?: string;
  contactSeparator?: string;
  contactAt?: IContactAt[];
}

export interface IContactAt {
  label?: string;
  value?: string;
}


export interface TablelistStateListItemConfig extends ITablelistStateListItemConfig{
  exportConfiguration?: ExportConfigInterface;
}

export interface ExportConfigInterface {
  buttonTitle: string;
  options: Partial<ExportConfig>[];
}

export interface ExportConfig {
  label: string;
  type: 'allFieldsExport' | 'currentFieldsExport' | 'generic';
  ApiResource: ApiOptions;
  hideForDefaultFilter?: boolean
}

export interface ApiOptions {
  url: string;
  httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE';
  refreshWorklist: boolean;
  export? :boolean;
  exportType? : 'txt';
  exportTitle? : string;
  maxExportLimit?: number;
  payloadData: ApiConfigPayload ;
}

export interface ApiConfigPayload {
  /** use array or string type to pick fields from worklist with same name.
   *
   *  Object is used to pick property with alias names
   *  { 'pathToPick' : 'aliasPropertyName' }
   *
   *  If property is not specified it will pick the selected diagnostic order Id's by default
   **/
  pickFromWorklist: string | Array<string> | object ;
  identifier: string;       // Identifier is the Property which will be used to map the picked from worklist
  [prop: string]: string | Array<string> | object;
}

